import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  MenuItem,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
  Tooltip
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { authGet, authPost } from '../../lib/api';
import { formatDateTime } from '../../lib/dateFormat';

export default function AiKeysPage() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [issueOpen, setIssueOpen] = useState(false);
  const [newKey, setNewKey] = useState({ user_id: '', days: 180 });
  const [issuing, setIssuing] = useState(false);

  const [issued, setIssued] = useState(null);
  const [copied, setCopied] = useState(false);
  const [users, setUsers] = useState([]);

  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revoking, setRevoking] = useState(false);
  const [scopeTarget, setScopeTarget] = useState(null);
  const [scope, setScope] = useState('read');
  const [savingScope, setSavingScope] = useState(false);
  const [scopeError, setScopeError] = useState(null);

  const scopeLabel = value => value === 'write' ? 'Read/write' : value === 'read' ? 'Read-only' : value;

  const handleScopeSave = async () => {
    if (!scopeTarget || savingScope) return;
    setSavingScope(true);
    setScopeError(null);
    setSuccess(null);
    try {
      await authPost(`/api/admin/ai-keys/keys/${scopeTarget.id}/scope`, { scope });
      setSuccess(`Scope for ${scopeTarget.owner_label} updated to ${scopeLabel(scope)}. The key remains the same.`);
      setScopeTarget(null);
      await loadKeys();
    } catch (err) {
      setScopeError(err.message || 'Unable to update key scope');
    } finally {
      setSavingScope(false);
    }
  };

  const loadKeys = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await authGet('/api/admin/ai-keys/keys');
      setKeys(data.keys || []);
    } catch (err) {
      setError(err.message || 'Unable to load AI keys');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const data = await authGet('/api/admin/ai-keys/users');
      setUsers(data.users || []);
    } catch {
      setUsers([]);
    }
  };

  useEffect(() => {
    loadKeys();
    loadUsers();
  }, []);

  const handleIssue = async () => {
    setIssuing(true);
    setError(null);
    setSuccess(null);
    try {
      const data = await authPost('/api/admin/ai-keys/keys', {
        user_id: newKey.user_id,
        days: Number(newKey.days)
      });
      setIssued(data);
      setIssueOpen(false);
      setNewKey({ user_id: '', days: 180 });
      await loadKeys();
    } catch (err) {
      setError(err.message || 'Unable to issue key');
    } finally {
      setIssuing(false);
    }
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    setError(null);
    setSuccess(null);
    try {
      await authPost(`/api/admin/ai-keys/keys/${revokeTarget.id}/revoke`);
      setSuccess(`Key for ${revokeTarget.owner_label} revoked`);
      setRevokeTarget(null);
      await loadKeys();
    } catch (err) {
      setError(err.message || 'Unable to revoke key');
    } finally {
      setRevoking(false);
    }
  };

  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(issued.key);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const statusChip = key => {
    if (key.revoked_at) return <Chip size="small" color="error" label="Revoked" />;
    if (new Date(key.expires_at) <= new Date()) return <Chip size="small" color="warning" label="Expired" />;
    return <Chip size="small" color="success" label="Active" />;
  };

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">AI Access Keys</Typography>
        <Button variant="contained" onClick={() => setIssueOpen(true)}>
          Issue Key
        </Button>
      </Box>

      <Typography variant="body2" color="textSecondary" mb={2}>
        Keys let a person's AI assistant access Alamo class data over MCP. New keys are read-only;
        administrators can edit the scope of active keys.
        Give the key to the user over a secure channel; it is shown only once when issued.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Owner</TableCell>
              <TableCell>Scope</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Expires</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {keys.map(key => (
              <TableRow key={key.id}>
                <TableCell>{key.owner_label}</TableCell>
                <TableCell>
                  <Chip size="small" label={scopeLabel(key.scope)} color={key.scope === 'write' ? 'secondary' : 'default'} />
                </TableCell>
                <TableCell>{formatDateTime(key.created_at)}</TableCell>
                <TableCell>{formatDateTime(key.expires_at)}</TableCell>
                <TableCell>{statusChip(key)}</TableCell>
                <TableCell align="right">
                  {!key.revoked_at && new Date(key.expires_at) > new Date() && (
                    <Button size="small" onClick={() => {
                      setScopeTarget(key);
                      setScope(key.scope);
                      setScopeError(null);
                    }}>
                      Edit scope
                    </Button>
                  )}
                  {!key.revoked_at && (
                    <Button size="small" color="error" onClick={() => setRevokeTarget(key)}>
                      Revoke
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!loading && keys.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>No AI keys issued yet.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {loading && (
          <Box p={2}>
            <Typography variant="body2">Loading...</Typography>
          </Box>
        )}
      </Paper>

      {/* Scope edit dialog */}
      <Dialog open={Boolean(scopeTarget)} onClose={() => !savingScope && setScopeTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit AI key scope</DialogTitle>
        <DialogContent>
          <Typography>{scopeTarget && `Owner: ${scopeTarget.owner_label}`}</Typography>
          <Alert severity="warning" sx={{ my: 2 }}>
            Read/write access lets the user's AI propose supported changes, ask that same user to confirm
            in conversation, then call execute_confirmed_change with confirmed: true using their personal
            write credential. No separate administrator message or review is required. Read-only access is unchanged.
            Changing scope keeps the same key; the user does not need a replacement key.
          </Alert>
          {scopeError && <Alert severity="error" sx={{ mb: 2 }}>{scopeError}</Alert>}
          <TextField
            autoFocus
            select
            fullWidth
            margin="normal"
            label="Scope"
            value={scope}
            onChange={e => setScope(e.target.value)}
            disabled={savingScope}
          >
            <MenuItem value="read">Read-only</MenuItem>
            <MenuItem value="write">Read/write</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScopeTarget(null)} disabled={savingScope}>Cancel</Button>
          <Button onClick={handleScopeSave} variant="contained"
            disabled={savingScope || !scopeTarget || scope === scopeTarget.scope || !['read', 'write'].includes(scope)}>
            {savingScope ? 'Saving...' : 'Save scope'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Issue dialog */}
      <Dialog open={issueOpen} onClose={() => !issuing && setIssueOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Issue AI Access Key</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            select
            fullWidth
            margin="normal"
            label="Person"
            value={newKey.user_id}
            onChange={e => setNewKey({ ...newKey, user_id: e.target.value })}
          >
            {users.map(user => (
              <MenuItem key={user.id} value={user.id}>
                {user.full_name ? `${user.full_name} (${user.email})` : user.email}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            fullWidth
            margin="normal"
            label="Expires in"
            value={newKey.days}
            onChange={e => setNewKey({ ...newKey, days: e.target.value })}
          >
            {[30, 60, 90, 180].map(d => (
              <MenuItem key={d} value={d}>
                {d} days
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIssueOpen(false)} disabled={issuing}>
            Cancel
          </Button>
          <Button onClick={handleIssue} variant="contained" disabled={issuing || !newKey.user_id}>
            {issuing ? 'Issuing...' : 'Issue Key'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* One-time key display dialog */}
      <Dialog open={Boolean(issued)} onClose={() => setIssued(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Key issued — copy it now</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This key is shown only once and cannot be retrieved again. Copy it and send it to the user through
            your secure channel.
          </Alert>
          <TextField
            fullWidth
            multiline
            value={issued ? issued.key : ''}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={copied ? 'Copied' : 'Copy'}>
                    <IconButton onClick={copyKey}>
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              )
            }}
          />
          <Typography variant="body2" color="textSecondary" mt={1}>
            {issued && `Owner: ${issued.owner_label} · Scope: ${scopeLabel(issued.scope)} · Expires in ${issued.days} days`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIssued(null)} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revoke confirmation */}
      <Dialog open={Boolean(revokeTarget)} onClose={() => !revoking && setRevokeTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Revoke key?</DialogTitle>
        <DialogContent>
          <Typography>
            {revokeTarget && `Revoke the AI key for ${revokeTarget.owner_label}? The user's AI will immediately lose access on its next request.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeTarget(null)} disabled={revoking}>
            Cancel
          </Button>
          <Button onClick={handleRevoke} color="error" variant="contained" disabled={revoking}>
            {revoking ? 'Revoking...' : 'Revoke'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
